/**
 * AI Garage API Service
 * Provides methods for interacting with the AI Garage backend
 */

import api from '../api';
import { getBackendEnvironment } from '../../utils/environment';

const GARAGE_BASE = '/api/v1/garage';

/** Keep each attachment upload comfortably under nginx's client_max_body_size. */
const ATTACHMENT_BATCH_MAX_BYTES = 100 * 1024 * 1024;

/** Build dashboard date-range query params from UI filters */
function dashboardFilterParams(filters = {}) {
  const params = {
    date_range: filters.dateRange || '7d',
    tool_type: filters.toolType || 'all',
  };
  if (filters.dateRange === 'custom' && filters.customStartDate && filters.customEndDate) {
    params.start_date = filters.customStartDate;
    params.end_date = filters.customEndDate;
  }
  const environment = getBackendEnvironment();
  if (environment) {
    params.environment = environment;
  }
  return params;
}

export const garageAPI = {
  getPersonaWebhooks: async () => (await api.get(`${GARAGE_BASE}/admin/persona-webhooks`)).data,
  savePersonaWebhook: async (id, data) => (await api.put(`${GARAGE_BASE}/admin/persona-webhooks/${id}`, data)).data,
  removePersonaWebhook: async (id) => api.delete(`${GARAGE_BASE}/admin/persona-webhooks/${id}`),
  testPersonaWebhook: async (id) => (await api.post(`${GARAGE_BASE}/admin/persona-webhooks/${id}/test`)).data,
  // ==================== Tools ====================

  /**
   * Get all tools with optional filtering
   * @param {Object} params - Filter parameters
   * @param {boolean} skipEnvironmentFilter - If true, don't apply environment filter (for admin view)
   * @param {Object} axiosConfig - Optional axios config (e.g. { signal } for AbortController)
   * @returns {Promise} Tools array
   */
  getTools: async (params = {}, skipEnvironmentFilter = false, axiosConfig = {}) => {
    const requestParams = { ...params };
    if (skipEnvironmentFilter) {
      requestParams.all_environments = true;
    } else {
      const environment = getBackendEnvironment();
      if (environment) {
        requestParams.environment = environment;
      }
    }
    const response = await api.get(`${GARAGE_BASE}/tools`, {
      params: requestParams,
      ...axiosConfig,
    });
    return response.data;
  },

  /**
   * Get a specific tool by ID
   * @param {string} toolId - Tool ID
   * @returns {Promise} Tool object
   */
  getTool: async (toolId) => {
    const response = await api.get(`${GARAGE_BASE}/tools/${toolId}`);
    return response.data;
  },

  /**
   * Create a new tool
   * @param {Object} toolData - Tool data
   * @returns {Promise} Created tool
   */
  createTool: async (toolData) => {
    const response = await api.post(`${GARAGE_BASE}/tools`, toolData);
    return response.data;
  },

  /**
   * Upload a tile/image file. Returns { url, filename, storage, path }.
   * Use the returned url as tool imageUrl.
   * @param {File} file
   * @returns {Promise<{url: string, filename?: string, storage?: string}>}
   */
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Let the browser set multipart boundary (do not force application/json)
    const response = await api.post(`${GARAGE_BASE}/uploads/image`, formData, {
      headers: { 'Content-Type': undefined },
      transformRequest: [(data, headers) => {
        if (typeof FormData !== 'undefined' && data instanceof FormData) {
          if (headers && typeof headers.delete === 'function') {
            headers.delete('Content-Type');
          } else if (headers) {
            delete headers['Content-Type'];
          }
        }
        return data;
      }],
    });
    return response.data;
  },

  /**
   * Submit product feedback (Bug / Feature / Other) with optional screenshot.
   */
  submitFeedback: async ({
    feedbackType,
    relatedTo,
    comments,
    outputComments,
    screenshot,
    userName,
  }) => {
    const formData = new FormData();
    formData.append('feedback_type', feedbackType);
    if (relatedTo) formData.append('related_to', relatedTo);
    if (comments) formData.append('comments', comments);
    if (outputComments) formData.append('output_comments', outputComments);
    if (userName) formData.append('user_name', userName);
    if (screenshot) formData.append('screenshot', screenshot);
    const response = await api.post(`${GARAGE_BASE}/feedbacks`, formData, {
      headers: { 'Content-Type': undefined },
      transformRequest: [(data, headers) => {
        if (typeof FormData !== 'undefined' && data instanceof FormData) {
          if (headers && typeof headers.delete === 'function') {
            headers.delete('Content-Type');
          } else if (headers) {
            delete headers['Content-Type'];
          }
        }
        return data;
      }],
    });
    return response.data;
  },

  /**
   * List product feedbacks (admin / reviewer / sales_admin).
   */
  listFeedbacks: async ({ feedbackType, relatedTo, userEmail, limit = 50, offset = 0 } = {}) => {
    const params = { limit, offset };
    if (feedbackType) params.feedback_type = feedbackType;
    if (relatedTo) params.related_to = relatedTo;
    if (userEmail) params.user_email = userEmail;
    const response = await api.get(`${GARAGE_BASE}/feedbacks`, { params });
    return response.data;
  },

  /**
   * Get one product feedback by id.
   */
  getFeedback: async (feedbackId) => {
    const response = await api.get(`${GARAGE_BASE}/feedbacks/${feedbackId}`);
    return response.data;
  },

  /**
   * Update a tool
   * @param {string} toolId - Tool ID
   * @param {Object} toolData - Updated tool data
   * @returns {Promise} Updated tool
   */
  updateTool: async (toolId, toolData) => {
    const response = await api.put(`${GARAGE_BASE}/tools/${toolId}`, toolData);
    return response.data;
  },

  getBotIntegration: async (toolId) => {
    const response = await api.get(`${GARAGE_BASE}/admin/tools/${toolId}/bot-integration`);
    return response.data;
  },

  lookupBotCredential: async (identifier) => {
    const response = await api.get(`${GARAGE_BASE}/admin/bot-credentials/lookup`, {
      params: { identifier },
    });
    return response.data;
  },

  testBotCredential: async (testData) => {
    const response = await api.post(`${GARAGE_BASE}/admin/bot-credentials/test`, testData);
    return response.data;
  },

  saveBotIntegration: async (toolId, integrationData) => {
    const response = await api.put(
      `${GARAGE_BASE}/admin/tools/${toolId}/bot-integration`,
      integrationData
    );
    return response.data;
  },

  testBotIntegration: async (toolId, testData = {}) => {
    const response = await api.post(
      `${GARAGE_BASE}/admin/tools/${toolId}/bot-integration/test`,
      testData
    );
    return response.data;
  },

  deleteBotIntegration: async (toolId) => {
    await api.delete(`${GARAGE_BASE}/admin/tools/${toolId}/bot-integration`);
  },

  resolveBotLaunch: async (toolId, target, conversationIdHint = null) => {
    const response = await api.post(`${GARAGE_BASE}/tools/${toolId}/launch`, {
      target,
      conversationIdHint: conversationIdHint || null,
    });
    return response.data;
  },

  /**
   * Delete a tool
   * @param {string} toolId - Tool ID
   * @returns {Promise} Success response
   */
  deleteTool: async (toolId) => {
    const response = await api.delete(`${GARAGE_BASE}/tools/${toolId}`);
    return response.data;
  },

  /**
   * Get tool usage and rating statistics
   * @param {string} toolId - Tool ID
   * @param {object} filters - Optional date range filters (dateRange, customStartDate, customEndDate)
   * @returns {Promise} Stats object with sessionsInRange, sessionsTotal, rating, ratingCount
   */
  getToolStats: async (toolId, filters = {}) => {
    const params = dashboardFilterParams(filters);
    const response = await api.get(`${GARAGE_BASE}/tools/${toolId}/stats`, { params });
    return response.data;
  },

  // ==================== Session Configs ====================

  /**
   * Create session config for a tool
   * @param {Object} configData - Session config data
   * @returns {Promise} Config response
   */
  createSessionConfig: async (configData) => {
    const response = await api.post(`${GARAGE_BASE}/session-configs`, configData);
    return response.data;
  },

  /**
   * Get session config by tool ID
   * @param {string} toolId - Tool ID
   * @returns {Promise} Session config
   */
  getSessionConfigByTool: async (toolId) => {
    const response = await api.get(`${GARAGE_BASE}/session-configs/tool/${toolId}`);
    return response.data;
  },

  /**
   * Update session config
   * @param {string} configId - Config ID
   * @param {Object} updateData - Update data
   * @returns {Promise} Updated config
   */
  updateSessionConfig: async (configId, updateData) => {
    const response = await api.put(`${GARAGE_BASE}/session-configs/${configId}`, updateData);
    return response.data;
  },

  /**
   * Test session config connection
   * @param {Object} testData - Test connection data
   * @returns {Promise} Test result
   */
  testSessionConnection: async (testData) => {
    const response = await api.post(`${GARAGE_BASE}/session-configs/test-connection`, testData);
    return response.data;
  },

  // ==================== Dashboard Analytics ====================

  /**
   * Get dashboard statistics
   * @param {object} filters - Filter options (dateRange, toolType, customStartDate, customEndDate)
   * @returns {Promise} Dashboard stats
   */
  getDashboardStats: async (filters = {}) => {
    const params = dashboardFilterParams(filters);
    const response = await api.get(`${GARAGE_BASE}/dashboard/stats`, { params });
    return response.data;
  },

  /**
   * Get top tools by usage
   * @param {number} limit - Number of tools to return
   * @param {object} filters - Filter options (dateRange, toolType, customStartDate, customEndDate)
   * @returns {Promise} Top tools
   */
  getTopToolsByUsage: async (limit = 10, filters = {}) => {
    const params = {
      limit,
      ...dashboardFilterParams(filters),
    };
    const response = await api.get(`${GARAGE_BASE}/dashboard/top-tools`, { params });
    return response.data;
  },

  /**
   * Get daily session trends for the selected date range
   * @param {object} filters - Filter options (dateRange, toolType, customStartDate, customEndDate)
   * @returns {Promise} Daily trends
   */
  getDailySessionTrends: async (filters = {}) => {
    const params = dashboardFilterParams(filters);
    const response = await api.get(`${GARAGE_BASE}/dashboard/daily-trends`, { params });
    return response.data;
  },

  /**
   * Get weekly session trends
   * @param {number} weeks - Number of weeks
   * @param {object} filters - Optional filters (dateRange, customStartDate, customEndDate)
   * @returns {Promise} Weekly trends
   */
  getWeeklySessionTrends: async (weeks = 8, filters = {}) => {
    const params = { weeks };
    if (filters.dateRange === 'custom' && filters.customStartDate && filters.customEndDate) {
      params.start_date = filters.customStartDate;
      params.end_date = filters.customEndDate;
    }
    const response = await api.get(`${GARAGE_BASE}/dashboard/weekly-trends`, { params });
    return response.data;
  },

  /**
   * Get weekly trends for multiple specific tools
   * @param {Array<string>} toolIds - Array of tool IDs
   * @param {number} weeks - Number of weeks
   * @returns {Promise} Multi-tool trends object { toolId: [...trends] }
   */
  getMultiToolWeeklyTrends: async (toolIds, filters = {}) => {
    if (!toolIds || toolIds.length === 0) {
      return {};
    }
    const response = await api.get(`${GARAGE_BASE}/dashboard/multi-tool-trends`, {
      params: {
        tool_ids: toolIds.join(','),
        ...dashboardFilterParams(filters),
      }
    });
    return response.data;
  },

  /**
   * Search users by name or email across all usage data
   * @param {string} query - Search query
   * @param {object} filters - Filter options (dateRange, toolType, customStartDate, customEndDate)
   * @param {number} limit - Maximum results
   * @returns {Promise} Matching users
   */
  searchUsersByUsage: async (query, filters = {}, limit = 50) => {
    const params = {
      q: query,
      limit,
      ...dashboardFilterParams(filters),
    };
    const response = await api.get(`${GARAGE_BASE}/dashboard/search-users`, { params });
    return response.data;
  },

  /**
   * Get top users by usage
   * @param {number} limit - Number of users to return
   * @param {object} filters - Filter options (dateRange, toolType, customStartDate, customEndDate)
   * @returns {Promise} Top users
   */
  getTopUsersByUsage: async (limit = 10, filters = {}) => {
    const params = {
      limit,
      ...dashboardFilterParams(filters),
    };
    const response = await api.get(`${GARAGE_BASE}/dashboard/top-users`, { params });
    return response.data;
  },

  /**
   * Get top users for a specific tool
   * @param {string} toolId - Tool ID
   * @param {number} limit - Number of users to return
   * @param {string} dateRange - Date range filter
   * @returns {Promise} Top users for the tool
   */
  getToolTopUsers: async (toolId, limit = 10, filters = {}) => {
    const filterObj = typeof filters === 'string' ? { dateRange: filters } : filters;
    const params = {
      limit,
      ...dashboardFilterParams(filterObj),
    };
    const response = await api.get(`${GARAGE_BASE}/tools/${toolId}/top-users`, { params });
    return response.data;
  },

  /**
   * Get tools used by a specific user
   * @param {string} userEmail - User email
   * @param {number} limit - Number of tools to return
   * @param {string} dateRange - Date range filter
   * @param {string} toolType - Tool type filter (all, Gem, App)
   * @returns {Promise} Tools used by the user with session counts and dates
   */
  getUserTools: async (userEmail, limit = 50, filtersOrDateRange = '7d', toolType = 'all') => {
    const filters = typeof filtersOrDateRange === 'object'
      ? { ...filtersOrDateRange, toolType: filtersOrDateRange.toolType || toolType }
      : { dateRange: filtersOrDateRange, toolType };
    const params = {
      limit,
      ...dashboardFilterParams(filters),
    };
    const response = await api.get(`${GARAGE_BASE}/users/${encodeURIComponent(userEmail)}/tools`, { params });
    return response.data;
  },

  // ==================== Favorites ====================

  /**
   * Get user's favorite tool IDs
   * @param {string} userEmail - User email
   * @returns {Promise} Array of tool IDs
   */
  getUserFavorites: async (userEmail) => {
    const response = await api.get(`${GARAGE_BASE}/favorites/${encodeURIComponent(userEmail)}`);
    return response.data;
  },

  /**
   * Add a favorite
   * @param {Object} favoriteData - {toolId, userEmail}
   * @returns {Promise} Created favorite
   */
  addFavorite: async (favoriteData) => {
    const response = await api.post(`${GARAGE_BASE}/favorites`, favoriteData);
    return response.data;
  },

  /**
   * Remove a favorite
   * @param {string} toolId - Tool ID
   * @param {string} userEmail - User email
   * @returns {Promise} Success response
   */
  removeFavorite: async (toolId, userEmail) => {
    const response = await api.delete(
      `${GARAGE_BASE}/favorites/${toolId}/${encodeURIComponent(userEmail)}`
    );
    return response.data;
  },

  // ==================== Ideas ====================

  /**
   * Get all ideas with optional filters
   * @param {Object} params - Filter parameters (status, persona, user_email)
   * @returns {Promise} Ideas array
   */
  getIdeas: async (params = {}) => {
    const response = await api.get(`${GARAGE_BASE}/ideas`, { params });
    return response.data;
  },

  /**
   * Get ideas by user email
   * @param {string} userEmail - User email
   * @returns {Promise} Ideas array
   */
  getIdeasByUser: async (userEmail) => {
    const response = await api.get(`${GARAGE_BASE}/ideas`, {
      params: { user_email: userEmail }
    });
    return response.data;
  },

  /**
   * Get idea statistics
   * @param {string|null} userEmail - Optional user email to filter stats
   * @returns {Promise} Stats object
   */
  getIdeaStats: async (userEmail = null) => {
    const params = userEmail ? { user_email: userEmail } : {};
    const response = await api.get(`${GARAGE_BASE}/ideas/stats`, { params });
    return response.data;
  },

  /**
   * Get a specific idea by ID
   * @param {string} ideaId - Idea ID
   * @returns {Promise} Idea object
   */
  getIdea: async (ideaId) => {
    const response = await api.get(`${GARAGE_BASE}/ideas/${ideaId}`);
    return response.data;
  },

  /**
   * Get LLM-suggested makers for a request
   * @param {Object} requestData - { problem: string, audience: { personas: [], reach: string, valueDriver: string } }
   * @returns {Promise} Maker suggestions
   */
  suggestMakers: async (requestData) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/suggest-makers`, requestData);
    return response.data;
  },

  /**
   * Create a new idea
   * @param {Object} ideaData - Idea data
   * @returns {Promise} Created idea
   */
  createIdea: async (ideaData) => {
    const response = await api.post(`${GARAGE_BASE}/ideas`, ideaData);
    return response.data;
  },

  /**
   * Update an idea
   * @param {string} ideaId - Idea ID
   * @param {Object} ideaData - Updated idea data
   * @returns {Promise} Updated idea
   */
  updateIdea: async (ideaId, ideaData) => {
    const response = await api.put(`${GARAGE_BASE}/ideas/${ideaId}`, ideaData);
    return response.data;
  },

  /**
   * Vote on an idea
   * @param {string} ideaId - Idea ID
   * @param {Object} voteData - {userEmail, voteType: 'upvote' | 'downvote'}
   * @returns {Promise} Updated idea
   */
  voteIdea: async (ideaId, voteData) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/${ideaId}/vote`, voteData);
    return response.data;
  },

  /**
   * Delete an idea
   * @param {string} ideaId - Idea ID
   * @returns {Promise} Success response
   */
  deleteIdea: async (ideaId) => {
    const response = await api.delete(`${GARAGE_BASE}/ideas/${ideaId}`);
    return response.data;
  },

  // ==================== Request Attachments ====================

  getIdeaAttachments: async (ideaId) => {
    const response = await api.get(`${GARAGE_BASE}/ideas/${ideaId}/attachments`);
    return response.data;
  },

  uploadIdeaAttachments: async (ideaId, files) => {
    const postBatch = async (batch) => {
      const formData = new FormData();
      batch.forEach((file) => formData.append('files', file));
      const response = await api.post(
        `${GARAGE_BASE}/ideas/${ideaId}/attachments`,
        formData,
        {
          headers: { 'Content-Type': undefined },
          transformRequest: [(data, headers) => {
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
              if (headers && typeof headers.delete === 'function') {
                headers.delete('Content-Type');
              } else if (headers) {
                delete headers['Content-Type'];
              }
            }
            return data;
          }],
        }
      );
      return response.data;
    };

    // The number of files is unbounded, so split them into batches that stay
    // under the proxy's request body limit instead of one huge multipart post.
    const uploaded = [];
    let batch = [];
    let batchBytes = 0;
    for (const file of files) {
      if (batch.length > 0 && batchBytes + file.size > ATTACHMENT_BATCH_MAX_BYTES) {
        uploaded.push(...(await postBatch(batch)));
        batch = [];
        batchBytes = 0;
      }
      batch.push(file);
      batchBytes += file.size;
    }
    if (batch.length > 0) {
      uploaded.push(...(await postBatch(batch)));
    }
    return uploaded;
  },

  downloadIdeaAttachment: async (ideaId, attachmentId) => {
    const response = await api.get(
      `${GARAGE_BASE}/ideas/${ideaId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  deleteIdeaAttachment: async (ideaId, attachmentId) => {
    await api.delete(
      `${GARAGE_BASE}/ideas/${ideaId}/attachments/${attachmentId}`
    );
  },

  // ==================== Comments ====================

  /**
   * Get comments for an idea
   * @param {string} ideaId - Idea ID
   * @returns {Promise} Comments array
   */
  getComments: async (ideaId) => {
    const response = await api.get(`${GARAGE_BASE}/ideas/${ideaId}/comments`);
    return response.data;
  },

  /**
   * Create a comment
   * @param {Object} commentData - {ideaId, text, authorEmail, authorName}
   * @returns {Promise} Created comment
   */
  createComment: async (commentData) => {
    const response = await api.post(`${GARAGE_BASE}/comments`, commentData);
    return response.data;
  },

  /**
   * Full history for a request: workflow actions and comments, oldest first.
   */
  getActivity: async (ideaId) => {
    const response = await api.get(`${GARAGE_BASE}/ideas/${ideaId}/activity`);
    return response.data;
  },

  // ==================== Reviews ====================

  /**
   * Get reviews for a tool
   * @param {string} toolId - Tool ID
   * @returns {Promise} Reviews array
   */
  getReviews: async (toolId) => {
    const response = await api.get(`${GARAGE_BASE}/tools/${toolId}/reviews`);
    return response.data;
  },

  /**
   * Create or update a review
   * @param {Object} reviewData - {toolId, userEmail, userName, rating, text}
   * @returns {Promise} Created/updated review
   */
  createReview: async (reviewData) => {
    const response = await api.post(`${GARAGE_BASE}/reviews`, reviewData);
    return response.data;
  },

  // ==================== Usage Logs ====================

  /**
   * Log tool usage
   * @param {string} toolId - Tool ID
   * @param {string} userEmail - User email
   * @returns {Promise} Log entry
   */
  logUsage: async (toolId, userEmail) => {
    const response = await api.post(
      `${GARAGE_BASE}/usage/${toolId}/${encodeURIComponent(userEmail)}`
    );
    return response.data;
  },

  /**
   * Get usage statistics
   * @returns {Promise} Usage stats object
   */
  getUsageStats: async () => {
    const response = await api.get(`${GARAGE_BASE}/usage/stats`);
    return response.data;
  },

  // ==================== Makers ====================

  /**
   * Get all makers
   * @param {Object} params - Filter parameters (e.g., {is_active: true})
   * @returns {Promise} Makers array
   */
  getMakers: async (params = {}) => {
    const response = await api.get(`${GARAGE_BASE}/makers`, { params });
    return response.data;
  },

  /**
   * Get a specific maker by email
   * @param {string} email - Maker email
   * @returns {Promise} Maker object with tools
   */
  getMaker: async (email) => {
    const response = await api.get(`${GARAGE_BASE}/makers/${encodeURIComponent(email)}`);
    return response.data;
  },

  /**
   * Create a new maker
   * @param {Object} makerData - Maker data
   * @returns {Promise} Created maker
   */
  createMaker: async (makerData) => {
    const response = await api.post(`${GARAGE_BASE}/makers`, makerData);
    return response.data;
  },

  /**
   * Update a maker
   * @param {string} email - Maker email
   * @param {Object} makerData - Updated maker data
   * @returns {Promise} Updated maker
   */
  updateMaker: async (email, makerData) => {
    const response = await api.put(`${GARAGE_BASE}/makers/${encodeURIComponent(email)}`, makerData);
    return response.data;
  },

  /**
   * Delete a maker
   * @param {string} email - Maker email
   * @returns {Promise} Success
   */
  deleteMaker: async (email) => {
    const response = await api.delete(`${GARAGE_BASE}/makers/${encodeURIComponent(email)}`);
    return response.data;
  },

  // ==================== User-Specific ====================

  getUserUsedToolIds: async (email) => {
    const response = await api.get(`${GARAGE_BASE}/users/${encodeURIComponent(email)}/used-tool-ids`);
    return response.data;
  },

  /**
   * Get user's recently used tools
   * @param {string} email - User email
   * @param {number} limit - Number of recent tools to return (default: 5)
   * @returns {Promise} Recent tools array
   */
  getUserRecentTools: async (email, limit = 5) => {
    const response = await api.get(`${GARAGE_BASE}/users/${encodeURIComponent(email)}/recent-tools`, {
      params: { limit }
    });
    return response.data;
  },

  // ==================== Pins ====================

  /**
   * Get user's pinned tools
   * @param {string} email - User email
   * @returns {Promise} Pinned tools array
   */
  getUserPins: async (email) => {
    const response = await api.get(`${GARAGE_BASE}/pins/${encodeURIComponent(email)}`);
    return response.data;
  },

  /**
   * Add a tool to user's pins
   * @param {Object} pinData - {toolId, userEmail}
   * @returns {Promise} Created pin
   */
  addPin: async (pinData) => {
    const response = await api.post(`${GARAGE_BASE}/pins`, pinData);
    return response.data;
  },

  /**
   * Remove a tool from user's pins
   * @param {string} toolId - Tool ID
   * @param {string} userEmail - User email
   * @returns {Promise} Success response
   */
  removePin: async (toolId, userEmail) => {
    const response = await api.delete(
      `${GARAGE_BASE}/pins/${toolId}/${encodeURIComponent(userEmail)}`
    );
    return response.data;
  },

  // ==================== Recent Tools ====================

  /**
   * Get user's recently accessed tools
   * @param {string} email - User email
   * @param {number} limit - Number of recent tools (default 5)
   * @returns {Promise} Recent tools array
   */
  getRecentTools: async (email, limit = 5) => {
    const response = await api.get(
      `${GARAGE_BASE}/recent/${encodeURIComponent(email)}?limit=${limit}`
    );
    return response.data;
  },

  /**
   * Log tool usage/launch
   * @param {string} toolId - Tool ID
   * @param {string} userEmail - User email
   * @returns {Promise} Success response
   */
  logToolUsage: async (toolId, userEmail) => {
    const response = await api.post(
      `${GARAGE_BASE}/usage/${toolId}?user_email=${encodeURIComponent(userEmail)}`
    );
    return response.data;
  },

  // ==================== Admin Routes ====================

  /**
   * Check if user is admin
   * @param {string} email - User email
   * @returns {Promise} Admin status object
   */
  checkAdminStatus: async (email) => {
    const response = await api.get(
      `${GARAGE_BASE}/admin/check/${encodeURIComponent(email)}`
    );
    return response.data;
  },

  getAdminActivity: async (params = {}) => {
    const response = await api.get(`${GARAGE_BASE}/admin/activity`, { params });
    return response.data;
  },

  /**
   * Publish a tool (Admin only)
   * @param {string} toolId - Tool ID
   * @returns {Promise} Updated tool
   */
  publishTool: async (toolId) => {
    const response = await api.put(`${GARAGE_BASE}/admin/tools/${toolId}/publish`);
    return response.data;
  },

  /**
   * Unpublish a tool (Admin only)
   * @param {string} toolId - Tool ID
   * @returns {Promise} Updated tool
   */
  unpublishTool: async (toolId) => {
    const response = await api.put(`${GARAGE_BASE}/admin/tools/${toolId}/unpublish`);
    return response.data;
  },

  /**
   * Create a new maker (Admin only)
   * @param {Object} makerData - Maker data
   * @returns {Promise} Created maker
   */
  createMakerAdmin: async (makerData) => {
    const response = await api.post(`${GARAGE_BASE}/admin/makers`, makerData);
    return response.data;
  },

  // ==================== User Profile Routes ====================

  /**
   * Get all user profiles (Admin only)
   * @returns {Promise} List of user profiles
   */
  getUserProfiles: async () => {
    const response = await api.get(`${GARAGE_BASE}/user-profiles`);
    return response.data;
  },

  /**
   * Get a specific user profile
   * @param {string} email - User email
   * @returns {Promise} User profile
   */
  getUserProfile: async (email) => {
    const response = await api.get(`${GARAGE_BASE}/user-profiles/${encodeURIComponent(email)}`);
    return response.data;
  },

  /**
   * Create a new user profile (Admin only)
   * @param {Object} profileData - User profile data
   * @returns {Promise} Created user profile
   */
  createUserProfile: async (profileData) => {
    const response = await api.post(`${GARAGE_BASE}/user-profiles`, profileData);
    return response.data;
  },

  /**
   * Update a user profile (Admin only)
   * @param {string} email - User email
   * @param {Object} profileData - Updated profile data
   * @returns {Promise} Updated user profile
   */
  updateUserProfile: async (email, profileData) => {
    const response = await api.put(`${GARAGE_BASE}/user-profiles/${encodeURIComponent(email)}`, profileData);
    return response.data;
  },

  emitGovernanceTelemetry: async (event) => {
    await api.post(`${GARAGE_BASE}/admin/governance-telemetry`, event);
  },

  /**
   * Delete a user profile (Admin only)
   * @param {string} email - User email
   * @returns {Promise} Success
   */
  deleteUserProfile: async (email) => {
    const response = await api.delete(`${GARAGE_BASE}/user-profiles/${encodeURIComponent(email)}`);
    return response.data;
  },

  /**
   * Record a successful login (bumps loginCount / lastLoginAt)
   * @param {string} email - User email
   * @returns {Promise} Updated user profile
   */
  recordUserLogin: async (email) => {
    const response = await api.post(
      `${GARAGE_BASE}/user-profiles/${encodeURIComponent(email)}/record-login`
    );
    return response.data;
  },

  /**
   * Workday Business_Unit for the signed-in user (onboarding department prefill)
   * @returns {Promise<{email: string, businessUnit: string|null, employeeName: string|null}>}
   */
  getMyWorkdayDepartment: async () => {
    const response = await api.get(`${GARAGE_BASE}/me/workday-department`);
    return response.data;
  },

  // ==================== Notifications ====================

  /**
   * Get notifications for a user
   * @param {string} userEmail - User email
   * @param {boolean} unreadOnly - Get only unread notifications
   * @param {number} limit - Max number of notifications
   * @returns {Promise} Notifications array
   */
  getNotifications: async (userEmail, unreadOnly = false, limit = 50) => {
    const response = await api.get(`${GARAGE_BASE}/notifications/${encodeURIComponent(userEmail)}`, {
      params: { unread_only: unreadOnly, limit }
    });
    return response.data;
  },

  /**
   * Get unread notification count for a user
   * @param {string} userEmail - User email
   * @returns {Promise} {count, userEmail}
   */
  getUnreadCount: async (userEmail) => {
    const response = await api.get(`${GARAGE_BASE}/notifications/${encodeURIComponent(userEmail)}/unread-count`);
    return response.data;
  },

  /**
   * Mark a notification as read
   * @param {string} notificationId - Notification ID
   * @param {string} userEmail - User email
   * @returns {Promise} Success
   */
  markNotificationAsRead: async (notificationId, userEmail) => {
    const response = await api.post(
      `${GARAGE_BASE}/notifications/${notificationId}/mark-read`,
      null,
      { params: { user_email: userEmail } }
    );
    return response.data;
  },

  /**
   * Mark all notifications as read
   * @param {string} userEmail - User email
   * @returns {Promise} {success, count}
   */
  markAllNotificationsAsRead: async (userEmail) => {
    const response = await api.post(
      `${GARAGE_BASE}/notifications/mark-all-read`,
      null,
      { params: { user_email: userEmail } }
    );
    return response.data;
  },

  /**
   * Delete a notification
   * @param {string} notificationId - Notification ID
   * @param {string} userEmail - User email
   * @returns {Promise} Success
   */
  deleteNotification: async (notificationId, userEmail) => {
    const response = await api.delete(
      `${GARAGE_BASE}/notifications/${notificationId}`,
      { params: { user_email: userEmail } }
    );
    return response.data;
  },

  // ==================== Request Info ====================

  /**
   * Admin requests more information from requestor
   * @param {string} ideaId - Idea/request ID
   * @param {Object} requestData - {adminEmail, adminName, message, priority}
   * @returns {Promise} {success, ideaStatus, notificationId, commentId}
   */
  requestMoreInfo: async (ideaId, requestData) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/${ideaId}/request-info`, requestData);
    return response.data;
  },

  /**
   * Admin routes a request to IT via Monday.com
   * @param {string} ideaId - Idea/request ID
   * @param {Object} routeData - {adminEmail, adminName, notes}
   * @returns {Promise} {success, ideaStatus, mondayItemId, mondayItemUrl, commentId}
   */
  routeToIT: async (ideaId, routeData) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/${ideaId}/route-to-it`, routeData);
    return response.data;
  },

  getItReview: async (ideaId) => {
    const response = await api.get(`${GARAGE_BASE}/ideas/${ideaId}/it-review`);
    return response.data;
  },

  grantItReviewAccess: async (ideaId, body = {}) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/${ideaId}/it-review/access`, body);
    return response.data;
  },

  /** Admin: start access/copy/scans from Initiated (held → pending). */
  initiateItReviewScans: async (ideaId, body = {}) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/${ideaId}/it-review/initiate`, body);
    return response.data;
  },

  /** Admin: ask requestor for repo URL and/or access token. */
  requestItReviewInput: async (ideaId, body = {}) => {
    const response = await api.post(
      `${GARAGE_BASE}/ideas/${ideaId}/it-review/request-input`,
      body
    );
    return response.data;
  },

  /** Admin: toggle non-repo / scans-not-applicable. */
  setItReviewNonRepo: async (ideaId, body = {}) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/${ideaId}/it-review/non-repo`, body);
    return response.data;
  },

  /** Admin: finish IT review and move the main status to Deploying. */
  completeItReview: async (ideaId, body = {}) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/${ideaId}/it-review/complete`, body);
    return response.data;
  },

  /** Admin: non-repo Initiate → Deploy → Release. */
  advanceItNonRepo: async (ideaId, body = {}) => {
    const response = await api.post(
      `${GARAGE_BASE}/ideas/${ideaId}/it-review/non-repo-advance`,
      body
    );
    return response.data;
  },

  rerunItReview: async (ideaId) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/${ideaId}/it-review/rerun`);
    return response.data;
  },

  waiveItReview: async (ideaId, body) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/${ideaId}/it-review/waive`, body);
    return response.data;
  },

  revokeItReviewWaiver: async (ideaId, body) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/${ideaId}/it-review/revoke-waiver`, body);
    return response.data;
  },

  updateItDevelopmentTodos: async (ideaId, body = {}) => {
    const response = await api.put(
      `${GARAGE_BASE}/ideas/${ideaId}/it-review/development-todos`,
      body
    );
    return response.data;
  },

  completeItDevelopment: async (ideaId, body = {}) => {
    const response = await api.post(
      `${GARAGE_BASE}/ideas/${ideaId}/it-review/complete-development`,
      body
    );
    return response.data;
  },

  /** Queue GitLab deploy on main for the worker (alias of complete-development). */
  initiateItDeployment: async (ideaId, body = {}) => {
    const response = await api.post(
      `${GARAGE_BASE}/ideas/${ideaId}/it-review/initiate-deployment`,
      body
    );
    return response.data;
  },

  returnItToScans: async (ideaId, body = {}) => {
    const response = await api.post(
      `${GARAGE_BASE}/ideas/${ideaId}/it-review/return-to-scans`,
      body
    );
    return response.data;
  },

  returnItToDevelopment: async (ideaId, body = {}) => {
    const response = await api.post(
      `${GARAGE_BASE}/ideas/${ideaId}/it-review/return-to-development`,
      body
    );
    return response.data;
  },

  /** After successful GitLab deploy, advance IT subflow to Release. */
  advanceItToRelease: async (ideaId, body = {}) => {
    const response = await api.post(
      `${GARAGE_BASE}/ideas/${ideaId}/it-review/advance-to-release`,
      body
    );
    return response.data;
  },

  /**
   * Start DOT AI Intake Assist for escalating a Sales request to IT BizOps
   * @param {string} ideaId
   * @param {Object} body - { requestedDeliveryDate? }
   */
  escalateToDotAssist: async (ideaId, body = {}) => {
    const response = await api.post(
      `${GARAGE_BASE}/ideas/${ideaId}/escalate-to-dot/assist`,
      body,
      { timeout: 130000 }
    );
    return response.data;
  },

  /**
   * Poll / resume DOT escalation draft state
   * @param {string} ideaId
   */
  escalateToDotStatus: async (ideaId) => {
    const response = await api.get(`${GARAGE_BASE}/ideas/${ideaId}/escalate-to-dot/status`);
    return response.data;
  },

  /**
   * Submit reviewed DOT draft (creates DOT request + write-back)
   * @param {string} ideaId
   * @param {Object} body - { draft, requestedDeliveryDate? }
   */
  escalateToDotSubmit: async (ideaId, body) => {
    const response = await api.post(
      `${GARAGE_BASE}/ideas/${ideaId}/escalate-to-dot/submit`,
      body,
      { timeout: 130000 }
    );
    return response.data;
  },

  /**
   * Publish a Gem to Discover from an admin-queue request
   * @param {string} ideaId
   * @param {Object} body - catalog fields (title, description, url, persona, …)
   * @returns {Promise} { success, alreadyPublished, idea, tool, commentId }
   */
  publishGemFromIdea: async (ideaId, body = {}) => {
    const response = await api.post(`${GARAGE_BASE}/ideas/${ideaId}/publish-gem`, body);
    return response.data;
  },

  // ==================== Config Options ====================

  /**
   * Get configuration options by category
   * @param {string} category - Category (role, journey, tag, capability, integration, persona, etc.)
   * @param {boolean} includeInactive - Include inactive options
   * @returns {Promise} Array of config options
   */
  getConfigOptions: async (category, includeInactive = false) => {
    const response = await api.get(`${GARAGE_BASE}/config-options/${category}`, {
      params: { include_inactive: includeInactive }
    });
    return response.data;
  },

  /**
   * Create a new configuration option (Admin only)
   * @param {Object} optionData - {category, value, label, description, displayOrder, isActive}
   * @returns {Promise} Created option
   */
  createConfigOption: async (optionData) => {
    const response = await api.post(`${GARAGE_BASE}/admin/config-options`, optionData);
    return response.data;
  },

  /**
   * List distinct Workday business units (Admin only)
   * @returns {Promise} [{businessUnit, employeeCount, exists}]
   */
  getWorkdayBusinessUnits: async () => {
    const response = await api.get(`${GARAGE_BASE}/admin/workday-business-units`);
    return response.data;
  },

  /**
   * List active Workday employees in selected Business Units (Admin only)
   * @param {string[]} businessUnits
   * @returns {Promise} Workday employee details for the audience picker
   */
  getWorkdayAudienceUsers: async (businessUnits) => {
    const response = await api.post(`${GARAGE_BASE}/admin/workday-audience-users`, {
      businessUnits,
    });
    return response.data;
  },

  /**
   * Create department options from Workday business units (Admin only)
   * @param {Object} payload - {businessUnits?: string[], parentId?: string}
   * @returns {Promise} {created, skipped}
   */
  importDepartmentsFromWorkday: async (payload = {}) => {
    const response = await api.post(`${GARAGE_BASE}/admin/config-options/import-departments`, payload);
    return response.data;
  },

  /**
   * Update a configuration option (Admin only)
   * @param {string} optionId - Option ID
   * @param {Object} optionData - Updated fields
   * @returns {Promise} Updated option
   */
  updateConfigOption: async (optionId, optionData) => {
    const response = await api.put(`${GARAGE_BASE}/admin/config-options/${optionId}`, optionData);
    return response.data;
  },

  /**
   * Delete a configuration option (Admin only)
   * @param {string} optionId - Option ID
   * @returns {Promise} Success response
   */
  deleteConfigOption: async (optionId) => {
    const response = await api.delete(`${GARAGE_BASE}/admin/config-options/${optionId}`);
    return response.data;
  },

  // ==================== ROI ====================

  /** Attach deploy environment (prd/test/dev) unless skipEnvironmentFilter. */
  _roiEnvParams: (params = {}, skipEnvironmentFilter = false) => {
    const requestParams = { ...params };
    if (skipEnvironmentFilter) {
      requestParams.all_environments = true;
    } else {
      const environment = getBackendEnvironment();
      if (environment) {
        requestParams.environment = environment;
      }
    }
    return requestParams;
  },

  getRoiProgram: async (params = {}, skipEnvironmentFilter = false) => {
    const response = await api.get(`${GARAGE_BASE}/roi/program`, {
      params: garageAPI._roiEnvParams(params, skipEnvironmentFilter),
    });
    return response.data;
  },

  getRoiTools: async (params = {}, skipEnvironmentFilter = false) => {
    const response = await api.get(`${GARAGE_BASE}/roi/tools`, {
      params: garageAPI._roiEnvParams(params, skipEnvironmentFilter),
    });
    return response.data;
  },

  getRoiUsers: async (params = {}, skipEnvironmentFilter = false) => {
    const response = await api.get(`${GARAGE_BASE}/roi/users`, {
      params: garageAPI._roiEnvParams(params, skipEnvironmentFilter),
    });
    return response.data;
  },

  /**
   * Per-user ROI contribution for a single tool (Tools Leaderboard drill-down),
   * sorted by time saved (most to least).
   * @param {string} toolId - Tool ID
   * @param {Object} params - { window, start_date, end_date, search, limit }
   * @param {boolean} skipEnvironmentFilter
   * @returns {Promise<Array<{userEmail, userName, sessions, timeSavedMin, isAutomation}>>}
   */
  getRoiToolUsers: async (toolId, params = {}, skipEnvironmentFilter = false) => {
    const response = await api.get(`${GARAGE_BASE}/roi/tools/${toolId}/users`, {
      params: garageAPI._roiEnvParams(params, skipEnvironmentFilter),
    });
    return response.data;
  },

  /**
   * Tools used by a specific user, with sessions, dates, and declared time saved
   * (Top Users row click drill-down on the Time Saved / ROI tab).
   * @param {string} userEmail - User email
   * @param {Object} params - { window, start_date, end_date }
   * @param {boolean} skipEnvironmentFilter
   * @returns {Promise<Array<{toolId, toolTitle, toolType, environment, roiStatus, minPerSession, sessionCount, timeSavedMin, firstAccessedAt, lastAccessedAt}>>}
   */
  getRoiUserTools: async (userEmail, params = {}, skipEnvironmentFilter = false) => {
    const response = await api.get(`${GARAGE_BASE}/roi/users/${encodeURIComponent(userEmail)}/tools`, {
      params: garageAPI._roiEnvParams(params, skipEnvironmentFilter),
    });
    return response.data;
  },

  getMyRoi: async (params = {}, skipEnvironmentFilter = false) => {
    const response = await api.get(`${GARAGE_BASE}/roi/users/me`, {
      params: garageAPI._roiEnvParams(params, skipEnvironmentFilter),
    });
    return response.data;
  },

  // ==================== My Team (Workday org + usage/ROI) ====================

  /**
   * Signed-in user's Workday context (hasDirectReports drives My Team tab visibility).
   * Authenticated via Bearer JWT like all other garage calls.
   */
  getMyTeamContext: async () => {
    const response = await api.get(`${GARAGE_BASE}/team/me`);
    return response.data;
  },

  /**
   * Org people search — dashboard roles only. Lets an admin open any manager's org.
   * @param {string} query - name or email fragment (min 2 chars)
   */
  searchOrgPeople: async (query, limit = 20) => {
    const response = await api.get(`${GARAGE_BASE}/team/search`, {
      params: { q: query, limit },
    });
    return response.data;
  },

  /**
   * Direct reports under managerEmail (default: caller) with session/ROI summary.
   * @param {Object} params - { manager_email?, window, start_date, end_date }
   */
  getTeamChildren: async (params = {}, skipEnvironmentFilter = false) => {
    const response = await api.get(`${GARAGE_BASE}/team/children`, {
      params: garageAPI._roiEnvParams(params, skipEnvironmentFilter),
    });
    return response.data;
  },

  /**
   * Person detail: Workday profile + usage tools + ROI tools.
   * Only allowed for self, span-of-control, or dashboard admin roles.
   */
  getTeamMemberDetail: async (memberEmail, params = {}, skipEnvironmentFilter = false) => {
    const response = await api.get(
      `${GARAGE_BASE}/team/members/${encodeURIComponent(memberEmail)}`,
      { params: garageAPI._roiEnvParams(params, skipEnvironmentFilter) }
    );
    return response.data;
  },

  updateToolRoi: async (toolId, data) => {
    const response = await api.patch(`${GARAGE_BASE}/roi/tools/${toolId}`, data);
    return response.data;
  },

  markToolRoiExempt: async (toolId, note = null) => {
    const response = await api.post(`${GARAGE_BASE}/roi/tools/${toolId}/exempt`, { note });
    return response.data;
  },

  unexemptToolRoi: async (toolId, note = null) => {
    const response = await api.delete(`${GARAGE_BASE}/roi/tools/${toolId}/exempt`, {
      params: note ? { note } : {},
    });
    return response.data;
  },

  getRoiAuditLog: async (params = {}) => {
    const response = await api.get(`${GARAGE_BASE}/roi/audit_log`, { params });
    return response.data;
  },

  getRoiSettings: async () => {
    const response = await api.get(`${GARAGE_BASE}/roi/settings`);
    return response.data;
  },

  updateRoiSettings: async (data) => {
    const response = await api.patch(`${GARAGE_BASE}/roi/settings`, data);
    return response.data;
  },

  exportRoiCsv: async (windowOrParams = '90d', skipEnvironmentFilter = false) => {
    const params =
      typeof windowOrParams === 'string' ? { window: windowOrParams } : { ...windowOrParams };
    const response = await api.get(`${GARAGE_BASE}/roi/export.csv`, {
      params: garageAPI._roiEnvParams(params, skipEnvironmentFilter),
      responseType: 'blob',
    });
    return response.data;
  },

  getRoiUnreported: async (params = {}, skipEnvironmentFilter = false) => {
    const response = await api.get(`${GARAGE_BASE}/roi/prompts/unreported`, {
      params: garageAPI._roiEnvParams(params, skipEnvironmentFilter),
    });
    return response.data;
  },

  submitRoiReports: async (reports) => {
    const response = await api.post(`${GARAGE_BASE}/roi/reports`, { reports });
    return response.data;
  },

  snoozeRoiPrompts: async (duration) => {
    const response = await api.post(`${GARAGE_BASE}/roi/prompts/snooze`, { duration });
    return response.data;
  },

  markRoiDigestShown: async () => {
    const response = await api.post(`${GARAGE_BASE}/roi/prompts/mark-shown`);
    return response.data;
  },

  // ==================== Access requests (Phase 1) ====================

  getAccessState: async (params = {}) => {
    const response = await api.get(`${GARAGE_BASE}/access/state`, { params });
    return response.data;
  },

  createAccessRequest: async (payload) => {
    const response = await api.post(`${GARAGE_BASE}/access/requests`, payload);
    return response.data;
  },

  getMyAccessRequests: async (params = {}) => {
    const response = await api.get(`${GARAGE_BASE}/access/requests/me`, { params });
    return response.data;
  },

  getAdminAccessRequests: async (params = {}) => {
    const response = await api.get(`${GARAGE_BASE}/access/requests`, { params });
    return response.data;
  },

  provisionAccessRequest: async (requestId) => {
    const response = await api.post(`${GARAGE_BASE}/access/requests/${requestId}/provision`);
    return response.data;
  },

  rejectAccessRequest: async (requestId, reason) => {
    const response = await api.post(`${GARAGE_BASE}/access/requests/${requestId}/reject`, { reason });
    return response.data;
  },

  listAccessResources: async (params = {}) => {
    const response = await api.get(`${GARAGE_BASE}/access/resources`, { params });
    return response.data;
  },

  upsertAccessResource: async (payload) => {
    const response = await api.post(`${GARAGE_BASE}/access/resources`, payload);
    return response.data;
  },

  getToolSpecificUsers: async (toolId) => {
    const response = await api.get(`${GARAGE_BASE}/admin/tools/${toolId}/specific-users`);
    return response.data;
  },

  // ==================== Security Scan ====================

  getSecScanSettings: async () => {
    const response = await api.get(`${GARAGE_BASE}/sec-scan/settings`);
    return response.data;
  },

  updateSecScanSettings: async (payload) => {
    const response = await api.put(`${GARAGE_BASE}/sec-scan/settings`, payload);
    return response.data;
  },

  updateSecScanRepo: async (repoId, payload) => {
    const response = await api.patch(`${GARAGE_BASE}/sec-scan/repos/${repoId}`, payload);
    return response.data;
  },

  listSecScanRepos: async (params = {}) => {
    const response = await api.get(`${GARAGE_BASE}/sec-scan/repos`, { params });
    return response.data;
  },

  createSecScanRepo: async (payload) => {
    const response = await api.post(`${GARAGE_BASE}/sec-scan/repos`, payload);
    return response.data;
  },

  grantSecScanRepoAccess: async (repoId, payload = {}) => {
    const response = await api.post(`${GARAGE_BASE}/sec-scan/repos/${repoId}/access`, payload);
    return response.data;
  },

  deleteSecScanRepo: async (repoId) => {
    await api.delete(`${GARAGE_BASE}/sec-scan/repos/${repoId}`);
  },

  listSecScanRuns: async (params = {}) => {
    const response = await api.get(`${GARAGE_BASE}/sec-scan/runs`, { params });
    return response.data;
  },

  startSecScanRun: async (repoId, payload) => {
    const response = await api.post(`${GARAGE_BASE}/sec-scan/repos/${repoId}/runs`, payload);
    return response.data;
  },

  getSecScanRun: async (runId) => {
    const response = await api.get(`${GARAGE_BASE}/sec-scan/runs/${runId}`);
    return response.data;
  },

  cancelSecScanRun: async (runId) => {
    const response = await api.post(`${GARAGE_BASE}/sec-scan/runs/${runId}/cancel`);
    return response.data;
  },

  deleteSecScanRun: async (runId) => {
    await api.delete(`${GARAGE_BASE}/sec-scan/runs/${runId}`);
  },

  getSecScanDashboardObjectUrl: async (runId) => {
    const response = await api.get(`${GARAGE_BASE}/sec-scan/runs/${runId}/dashboard`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },

  downloadSecScanArtifact: async (runId, kind, fileName) => {
    const response = await api.get(
      `${GARAGE_BASE}/sec-scan/runs/${runId}/artifacts/${kind}`,
      { responseType: 'blob' }
    );
    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName || kind;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  },
};

export default garageAPI;
